/**
 * HTTP client para a API do backend Sara.
 *
 * Auth: access token JWT em memória (não localStorage por XSS) +
 * refresh token em localStorage (chave de hash do refresh, não o JWT).
 * Quando access token expira (401), tenta refresh automático uma vez.
 */

// Marca selecionada (BR padrão / EN = instância em inglês / ES = instância em
// espanhol). O seletor no header grava `sara_api_base` no localStorage e
// recarrega; aqui lemos esse override pra apontar a API certa. Vazio/ausente =
// BR (VITE_API_URL).
const EN_API_URL = import.meta.env.VITE_EN_API_URL ?? '';
const ES_API_URL = import.meta.env.VITE_ES_API_URL ?? '';

export type Instancia = 'br' | 'en' | 'es';

/** Instância ativa. Um override cuja URL não foi configurada no build cai em BR,
 *  senão o painel apontaria pra uma API vazia. */
function instanciaAtual(): Instancia {
  try {
    const override = localStorage.getItem('sara_api_base');
    if (override === 'en' && EN_API_URL) return 'en';
    if (override === 'es' && ES_API_URL) return 'es';
  } catch {
    /* localStorage indisponível */
  }
  return 'br';
}

function resolveApiBase(): string {
  const atual = instanciaAtual();
  if (atual === 'en') return EN_API_URL;
  if (atual === 'es') return ES_API_URL;
  return import.meta.env.VITE_API_URL ?? '/api';
}
const API_BASE_URL = resolveApiBase();

// Refresh token POR instância (br/en/es): cada marca guarda o próprio login,
// então trocar de idioma no header NÃO desloga as outras. O operador alterna
// entre elas sem precisar logar de novo toda vez (pedido da dona 2026-07-07).
const REFRESH_KEY_PREFIX = 'sara_admin_refresh';
function refreshKey(): string {
  return `${REFRESH_KEY_PREFIX}_${instanciaAtual()}`;
}

let accessToken: string | null = null;
let refreshInFlight: Promise<string | null> | null = null;

/** Seletor de marca (instância). EN e ES só aparecem se a URL de cada uma
 *  (VITE_EN_API_URL / VITE_ES_API_URL) estiver setada no build. */
export const brand = {
  enAvailable(): boolean {
    return !!EN_API_URL;
  },
  esAvailable(): boolean {
    return !!ES_API_URL;
  },
  current(): Instancia {
    return instanciaAtual();
  },
  /** Troca a instância e recarrega. A sessão é POR instância, mas guardamos o
   *  refresh de cada uma separadamente (refreshKey), então NÃO apagamos o login
   *  aqui: alternar entre PT, EN e ES reaproveita o login existente de cada lado. */
  set(b: Instancia): void {
    try {
      if (b === 'br') localStorage.removeItem('sara_api_base');
      else localStorage.setItem('sara_api_base', b);
    } catch {
      /* ignore */
    }
    window.location.reload();
  },
};

export const tokens = {
  setAccess(token: string | null): void {
    accessToken = token;
  },
  getAccess(): string | null {
    return accessToken;
  },
  setRefresh(token: string | null): void {
    const k = refreshKey();
    if (token) localStorage.setItem(k, token);
    else localStorage.removeItem(k);
  },
  getRefresh(): string | null {
    return localStorage.getItem(refreshKey());
  },
};

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    message: string,
    public readonly body?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

interface RequestOptions {
  method?: string;
  body?: unknown;
  signal?: AbortSignal;
  /** Pula auth + refresh (ex: rota /auth/login). */
  skipAuth?: boolean;
}

export async function request<T = unknown>(path: string, options: RequestOptions = {}): Promise<T> {
  const { method = 'GET', body, signal, skipAuth = false } = options;

  const url = path.startsWith('http') ? path : `${API_BASE_URL}${path}`;

  const headers: Record<string, string> = {};
  if (body !== undefined) headers['Content-Type'] = 'application/json';
  if (!skipAuth && accessToken) headers.Authorization = `Bearer ${accessToken}`;

  const response = await fetch(url, {
    method,
    headers,
    body: body !== undefined ? JSON.stringify(body) : undefined,
    signal,
    credentials: 'include',
  });

  if (response.status === 401 && !skipAuth) {
    const newToken = await tryRefresh();
    if (newToken) {
      headers.Authorization = `Bearer ${newToken}`;
      const retryRes = await fetch(url, {
        method,
        headers,
        body: body !== undefined ? JSON.stringify(body) : undefined,
        signal,
        credentials: 'include',
      });
      return parseResponse<T>(retryRes);
    }
  }

  return parseResponse<T>(response);
}

async function parseResponse<T>(response: Response): Promise<T> {
  const text = await response.text();
  let parsed: unknown = null;
  if (text.length > 0) {
    try {
      parsed = JSON.parse(text);
    } catch {
      parsed = text;
    }
  }
  if (!response.ok) {
    const message =
      (parsed as { message?: string } | null)?.message ?? response.statusText ?? 'request error';
    throw new ApiError(response.status, message, parsed);
  }
  return parsed as T;
}

/**
 * Variante do `request` que devolve `Blob` em vez de JSON. Usado pra
 * baixar/visualizar PDFs autenticados — `<a href>` não envia o
 * Authorization header.
 */
export async function requestBlob(path: string, options: RequestOptions = {}): Promise<Blob> {
  const { method = 'GET', body, signal, skipAuth = false } = options;
  const url = path.startsWith('http') ? path : `${API_BASE_URL}${path}`;
  const headers: Record<string, string> = {};
  if (body !== undefined) headers['Content-Type'] = 'application/json';
  if (!skipAuth && accessToken) headers.Authorization = `Bearer ${accessToken}`;

  let response = await fetch(url, {
    method,
    headers,
    body: body !== undefined ? JSON.stringify(body) : undefined,
    signal,
    credentials: 'include',
  });

  if (response.status === 401 && !skipAuth) {
    const newToken = await tryRefresh();
    if (newToken) {
      headers.Authorization = `Bearer ${newToken}`;
      response = await fetch(url, {
        method,
        headers,
        body: body !== undefined ? JSON.stringify(body) : undefined,
        signal,
        credentials: 'include',
      });
    }
  }

  if (!response.ok) {
    const text = await response.text().catch(() => '');
    throw new ApiError(response.status, response.statusText || 'request error', text);
  }
  return response.blob();
}

async function tryRefresh(): Promise<string | null> {
  if (refreshInFlight) return refreshInFlight;

  const refreshToken = tokens.getRefresh();
  if (!refreshToken) return null;

  refreshInFlight = (async () => {
    try {
      const result = await request<{ accessToken: string; refreshToken: string }>('/auth/refresh', {
        method: 'POST',
        body: { refreshToken },
        skipAuth: true,
      });
      tokens.setAccess(result.accessToken);
      tokens.setRefresh(result.refreshToken);
      return result.accessToken;
    } catch {
      tokens.setAccess(null);
      tokens.setRefresh(null);
      return null;
    } finally {
      refreshInFlight = null;
    }
  })();

  return refreshInFlight;
}
